/**
 * AdaptiveQuestionService.js
 * 
 * Service để tạo câu hỏi verification thông minh dựa trên 
 * kết quả phân tích hình ảnh từ AI
 * 
 * Flow:
 * 1. AI phân tích ảnh → suspected_patterns
 * 2. Service này chọn câu hỏi phù hợp để verify
 * 3. User trả lời → Điều chỉnh confidence
 * 4. Final diagnosis với độ chính xác cao hơn
 */

import { VERIFICATION_QUESTIONS, VerificationHelper } from './tcm/VerificationQuestions';

class AdaptiveQuestionService {
    /**
     * Generate verification questions based on AI preliminary analysis
     * @param {Object} aiAnalysis - Kết quả phân tích sơ bộ từ AI
     * @returns {Object} - Questions to ask and metadata
     */
    static generateVerificationQuestions(aiAnalysis) {
        const questions = [];
        const askedPatterns = [];

        // Get suspected patterns from AI analysis
        const suspectedPatterns = aiAnalysis.suspected_patterns || [];

        // Filter patterns that need verification (confidence 40-80%)
        const patternsToVerify = suspectedPatterns
            .filter(p => p.confidence >= 0.3 && p.confidence <= 0.85)
            .slice(0, 3); // Max 3 patterns to verify

        if (patternsToVerify.length === 0) {
            // If no patterns need verification, return empty
            return {
                questions: [],
                patterns_to_verify: [],
                skip_verification: true,
                reason: 'AI confidence is already high enough'
            };
        }

        // Collect questions for each pattern
        patternsToVerify.forEach(pattern => {
            const patternId = pattern.id;
            const patternQuestions = VERIFICATION_QUESTIONS[patternId];

            if (patternQuestions) {
                // Take top 2-3 questions based on weight
                const sortedQuestions = [...patternQuestions]
                    .sort((a, b) => b.weight - a.weight)
                    .slice(0, 3);

                sortedQuestions.forEach(q => {
                    // Avoid duplicate questions
                    if (!questions.find(existing => existing.id === q.id)) {
                        questions.push({
                            ...q,
                            pattern_id: patternId,
                            pattern_name: pattern.name,
                            pattern_confidence: pattern.confidence
                        });
                    }
                });

                askedPatterns.push(patternId);
            }
        });

        // Deduplicate similar questions
        const deduplicatedQuestions = this._deduplicateSimilarQuestions(questions);

        // Limit to max 9 questions
        const finalQuestions = deduplicatedQuestions.slice(0, 9);

        return {
            questions: finalQuestions,
            patterns_to_verify: patternsToVerify,
            asked_patterns: askedPatterns,
            skip_verification: false,
            total_questions: finalQuestions.length
        };
    }

    /**
     * Remove similar questions (same meaning, different patterns)
     * @private
     */
    static _deduplicateSimilarQuestions(questions) {
        const seen = new Map();
        const result = [];

        // Keywords to detect similar questions
        const similarityKeywords = {
            fatigue: ['mệt mỏi', 'thiếu năng lượng', 'uể oải'],
            cold_hands: ['tay chân lạnh', 'lạnh tay'],
            sweating: ['đổ mồ hôi', 'mồ hôi'],
            dry_mouth: ['khô miệng', 'khô họng'],
            pain: ['đau nhức', 'đau'],
            insomnia: ['mất ngủ', 'khó ngủ']
        };

        questions.forEach(q => {
            const text = q.text.toLowerCase();
            let isDuplicate = false;

            // Check if similar question already exists
            Object.entries(similarityKeywords).forEach(([key, keywords]) => {
                const hasKeyword = keywords.some(kw => text.includes(kw));
                if (hasKeyword) {
                    if (seen.has(key)) {
                        isDuplicate = true;
                    } else {
                        seen.set(key, q.id);
                    }
                }
            });

            if (!isDuplicate) {
                result.push(q);
            }
        });

        return result;
    }

    /**
     * Process verification answers and update pattern confidences
     * @param {Object} originalAnalysis - AI's preliminary analysis
     * @param {Object} answers - User's answers to verification questions
     * @returns {Object} - Updated analysis with adjusted confidences
     */
    static processVerificationAnswers(originalAnalysis, answers) {
        const updatedPatterns = [];
        const verificationSummary = [];

        originalAnalysis.suspected_patterns.forEach(pattern => {
            const patternId = pattern.id;

            // Calculate verification score for this pattern
            const verificationScore = VerificationHelper.calculatePatternScore(patternId, answers);

            // Adjust confidence based on verification
            const originalConfidence = pattern.confidence;
            const newConfidence = VerificationHelper.adjustConfidence(
                originalConfidence,
                verificationScore
            );

            // Determine if verification confirmed or denied the pattern
            let verification_result = 'neutral';
            if (newConfidence > originalConfidence + 0.1) {
                verification_result = 'confirmed';
            } else if (newConfidence < originalConfidence - 0.1) {
                verification_result = 'denied';
            }

            updatedPatterns.push({
                ...pattern,
                original_confidence: originalConfidence,
                confidence: newConfidence,
                verification_score: verificationScore,
                verification_result
            });

            verificationSummary.push({
                pattern: pattern.name,
                before: (originalConfidence * 100).toFixed(0) + '%',
                after: (newConfidence * 100).toFixed(0) + '%',
                change: ((newConfidence - originalConfidence) * 100).toFixed(0) + '%',
                result: verification_result
            });
        });

        // Sort by new confidence
        updatedPatterns.sort((a, b) => b.confidence - a.confidence);

        return {
            ...originalAnalysis,
            suspected_patterns: updatedPatterns,
            verification_completed: true,
            verification_summary: verificationSummary,
            verification_answers: answers,
            verified_at: new Date().toISOString()
        };
    }

    /**
     * Get quick summary of what to verify
     * @param {Array} suspectedPatterns - AI's suspected patterns
     * @returns {Object} - Summary for UI display
     */
    static getVerificationSummary(suspectedPatterns) {
        const needsVerification = suspectedPatterns.filter(p =>
            p.confidence >= 0.3 && p.confidence <= 0.85
        );

        if (needsVerification.length === 0) {
            return {
                needs_verification: false,
                message: 'Phân tích đã đủ chính xác, không cần câu hỏi bổ sung.',
                patterns: []
            };
        }

        const patternNames = needsVerification.map(p => p.name).join(', ');

        return {
            needs_verification: true,
            message: `Chúng tôi cần xác nhận một vài triệu chứng để chẩn đoán chính xác hơn.`,
            patterns: needsVerification.map(p => ({
                name: p.name,
                confidence: (p.confidence * 100).toFixed(0) + '%',
                reason: p.reason
            })),
            estimated_questions: Math.min(needsVerification.length * 3, 9)
        };
    }

    /**
     * Format questions for UI display
     * @param {Array} questions - Questions from generateVerificationQuestions
     * @returns {Array} - Formatted questions for UI
     */
    static formatQuestionsForUI(questions) {
        return questions.map((q, index) => ({
            id: q.id,
            number: index + 1,
            text: q.text,
            type: q.type,
            options: q.type === 'frequency'
                ? [
                    { value: 'never', label: 'Không bao giờ' },
                    { value: 'rarely', label: 'Hiếm khi' },
                    { value: 'sometimes', label: 'Thỉnh thoảng' },
                    { value: 'often', label: 'Thường xuyên' },
                    { value: 'always', label: 'Luôn luôn' }
                ]
                : [
                    { value: 'no', label: 'Không' },
                    { value: 'yes', label: 'Có' }
                ],
            pattern_name: q.pattern_name,
            skip_allowed: true
        }));
    }
}

export default AdaptiveQuestionService;
